import React, {
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  AlertTriangle,
  MessageSquarePlus,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import type {
  AvailableClinicianResponse,
  PatientCommunicationResponse,
} from '../../services/api/types.gen';
import {
  client,
  createPatientCommunication,
  deletePatientCommunication,
  updatePatientCommunication,
} from '../../services/api';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';
import { formatDateToISO, formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { useToast } from '../../hooks/useToastContext';
import { LongTextPreview } from '../LongTextPreview';
import CommunicationVisitEditor from './CommunicationVisitEditor';
import {
  PATIENT_COMMUNICATION_TYPES,
  patientCommunicationTypeRequiresAssignableClinician,
} from './patientCommunicationTypes';
import type { PatientCommunicationTypeId } from '../../services/api/types.gen';
import { LockedTaskIndicator } from '../task/LockedTaskIndicator';
import { isScheduleLockedTask } from '../task/lockedTask';

const MISSING_PATIENT_CLINIC_MESSAGE =
  'Assign this patient to a clinic before adding a communication.';
const MISSING_PATIENT_CLINIC_DETAIL =
  'Communications need a clinic so physicians and assigned clinicians can be validated before saving.';

type PatientCommunicationsTabProps = {
  patientId: string;
  /** From parent patient record — filters physician / assigned-clinician dropdowns by clinic. */
  patientClinicId: string | null | undefined;
  communications: PatientCommunicationResponse[];
  communicationsLoading: boolean;
  onCommunicationsChange: Dispatch<
    SetStateAction<PatientCommunicationResponse[]>
  >;
  formatClinicianName: (c: AvailableClinicianResponse) => string;
  getClinicAssignableClinicians: (
    clinicId: string | null | undefined
  ) => AvailableClinicianResponse[];
  /** User_clinics with is_physician (not limited to assignable-clinician). */
  getClinicPhysicians: (
    clinicId: string | null | undefined
  ) => AvailableClinicianResponse[];
};

const PatientCommunicationsTab: React.FC<PatientCommunicationsTabProps> = ({
  patientId,
  patientClinicId,
  communications,
  communicationsLoading,
  onCommunicationsChange,
  formatClinicianName,
  getClinicAssignableClinicians,
  getClinicPhysicians,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [newDate, setNewDate] = useState<Date | undefined>(() => new Date());
  const [newCommType, setNewCommType] = useState<PatientCommunicationTypeId>(
    'Patient Communication'
  );
  const [newPhysicianId, setNewPhysicianId] = useState<string>('');
  const [newAssignee, setNewAssignee] = useState<string>('');
  const [newNotes, setNewNotes] = useState('');
  const [creating, setCreating] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editingCommunication, setEditingCommunication] =
    useState<PatientCommunicationResponse | null>(null);
  const [savingEditId, setSavingEditId] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const cliniciansForRows = useMemo(
    () => getClinicAssignableClinicians(patientClinicId),
    [getClinicAssignableClinicians, patientClinicId]
  );

  const physicianOptions = useMemo(
    () => getClinicPhysicians(patientClinicId),
    [getClinicPhysicians, patientClinicId]
  );

  const patientHasClinic = Boolean(patientClinicId);
  const addNeedsAssignableClinician =
    patientCommunicationTypeRequiresAssignableClinician(newCommType);
  const addBlockedByMissingClinic = !patientHasClinic;

  const sorted = useMemo(
    () =>
      [...communications].sort((a, b) =>
        (b.communication_date ?? '').localeCompare(a.communication_date ?? '')
      ),
    [communications]
  );

  const physicianNameForRow = (row: PatientCommunicationResponse) => {
    const named = row.physician_name?.trim();
    if (named) return named;
    const id = row.physician_user_clinic_id;
    if (!id) return '—';
    const c = physicianOptions.find(x => x.id === id);
    return c ? formatClinicianName(c) : '—';
  };

  const assigneeNameForRow = (assignedId: string | null | undefined) => {
    if (!assignedId) return '—';
    const c =
      cliniciansForRows.find(x => x.id === assignedId) ??
      physicianOptions.find(x => x.id === assignedId);
    return c ? formatClinicianName(c) : 'Unknown clinician';
  };

  const openEdit = (row: PatientCommunicationResponse) => {
    setEditingCommunication(row);
    setEditOpen(true);
  };

  const handleEditClose = (open: boolean) => {
    setEditOpen(open);
    if (!open) {
      setEditingCommunication(null);
      setSavingEditId(null);
    }
  };

  const handleSaveEdit = async (payload: {
    communication_date: string;
    communication_type: PatientCommunicationTypeId;
    assigned_clinician_id: string | null;
    physician_user_clinic_id: string;
    notes: string;
  }) => {
    if (!editingCommunication) return;
    setSavingEditId(editingCommunication.id);
    try {
      const res = await updatePatientCommunication({
        path: { communication_id: editingCommunication.id },
        body: {
          communication_date: payload.communication_date,
          communication_type: payload.communication_type,
          assigned_clinician_id: payload.assigned_clinician_id,
          physician_user_clinic_id: payload.physician_user_clinic_id,
          notes: payload.notes,
        },
        client,
      });
      const updated = res.data;
      if (!updated) {
        showError('Error', 'Could not save changes.');
        return;
      }
      onCommunicationsChange(prev =>
        prev.map(x => (x.id === updated.id ? updated : x))
      );
      showSuccess('Saved', 'Communication updated.');
      setEditOpen(false);
      setEditingCommunication(null);
    } catch (e) {
      console.error(e);
      showError('Error', 'Could not save changes.');
    } finally {
      setSavingEditId(null);
    }
  };

  const handleCreate = async () => {
    // Keep this as a defense-in-depth guard even though the Save button is
    // disabled for no-clinic patients; it prevents accidental API calls if the
    // button wiring changes.
    if (addBlockedByMissingClinic) {
      showError(
        'Validation',
        `${MISSING_PATIENT_CLINIC_MESSAGE} ${MISSING_PATIENT_CLINIC_DETAIL}`
      );
      return;
    }
    if (!newDate) {
      showError('Validation', 'Please select a date.');
      return;
    }
    if (!newPhysicianId) {
      showError('Validation', 'Please select a physician.');
      return;
    }
    if (addNeedsAssignableClinician && !newAssignee) {
      showError(
        'Validation',
        'Please select an assigned clinician for this type.'
      );
      return;
    }
    if (!newNotes.trim()) {
      showError('Validation', 'Please enter communication details.');
      return;
    }
    const commDate = formatDateToISO(newDate);
    if (!commDate) return;

    setCreating(true);
    try {
      const res = await createPatientCommunication({
        path: { patient_id: patientId },
        body: {
          communication_date: commDate,
          communication_type: newCommType,
          assigned_clinician_id: addNeedsAssignableClinician
            ? newAssignee
            : null,
          physician_user_clinic_id: newPhysicianId,
          notes: newNotes.trim(),
        },
        client,
      });
      const created = res.data;
      if (!created) {
        showError('Error', 'Could not create communication entry.');
        return;
      }
      onCommunicationsChange(prev => [...prev, created]);
      setNewNotes('');
      setNewAssignee('');
      setNewCommType('Patient Communication');
      setNewPhysicianId('');
      setNewDate(new Date());
      setAddOpen(false);
      showSuccess('Saved', 'Communication added successfully');
    } catch (e) {
      console.error(e);
      showError('Error', 'Could not create communication entry.');
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deletePatientCommunication({
        path: { communication_id: deleteId },
        client,
      });
      onCommunicationsChange(prev => prev.filter(x => x.id !== deleteId));
      if (editingCommunication?.id === deleteId) {
        setEditOpen(false);
        setEditingCommunication(null);
      }
      setDeleteId(null);
      showSuccess('Deleted', 'Communication entry removed.');
    } catch (e) {
      console.error(e);
      showError('Error', 'Could not delete entry.');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) =>
    formatDateToMMDDYYYY(dateString) || 'N/A';

  if (communicationsLoading) {
    return (
      <p className='text-sm text-muted-foreground'>Loading communications…</p>
    );
  }

  return (
    <div className='w-full min-w-0 space-y-4'>
      <Card className='border-border/70 shadow-sm'>
        <CardHeader className='flex flex-row items-center justify-end pb-3'>
          <Button
            size='sm'
            onClick={() => {
              setNewCommType('Patient Communication');
              setNewPhysicianId('');
              setNewAssignee('');
              setAddOpen(true);
            }}
            className='shrink-0 gap-1.5'
          >
            <Plus className='h-4 w-4' />
            Add Communication
          </Button>
        </CardHeader>
        <CardContent className='pt-0'>
          {sorted.length === 0 ? (
            <div className='flex flex-col items-center justify-center rounded-md border border-dashed border-border/60 bg-muted/15 py-12 text-center text-muted-foreground'>
              <MessageSquarePlus className='mb-3 h-10 w-10 opacity-40' />
              <p className='text-sm'>No communications yet.</p>
              <p className='text-sm mt-1'>
                Click &quot;Add Communication&quot; to add a communication
              </p>
            </div>
          ) : (
            <div className='overflow-hidden rounded-md border border-border/60 bg-background/40'>
              <div
                className='hidden lg:grid lg:grid-cols-[minmax(88px,0.7fr)_minmax(96px,1fr)_minmax(100px,0.9fr)_minmax(100px,0.9fr)_minmax(220px,1.6fr)_minmax(88px,auto)] items-center gap-3 border-b border-border/60 bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground'
                data-testid='communications-header'
              >
                <span>Date</span>
                <span>Type</span>
                <span>Physician</span>
                <span>Assigned clinician</span>
                <span>Communication</span>
                <span className='text-right'>Actions</span>
              </div>
              <div className='divide-y divide-border/60'>
                {sorted.map(row => {
                  const isLockedCommunication = isScheduleLockedTask({
                    task_type: 'patient_communication',
                    status: row.status,
                  });

                  return (
                    <div
                      key={row.id}
                      className='group px-3 py-2.5 transition-colors hover:bg-muted/20'
                    >
                      <div className='grid gap-2.5 lg:grid-cols-[minmax(88px,0.7fr)_minmax(96px,1fr)_minmax(100px,0.9fr)_minmax(100px,0.9fr)_minmax(220px,1.6fr)_minmax(88px,auto)] lg:items-center lg:gap-x-3'>
                        <div>
                          <span className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden'>
                            Date
                          </span>
                          <div className='text-sm font-medium text-foreground'>
                            {formatDate(row.communication_date)}
                          </div>
                        </div>
                        <div className='min-w-0'>
                          <span className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden'>
                            Type
                          </span>
                          <p
                            className='text-sm text-foreground line-clamp-2'
                            title={row.communication_type}
                          >
                            {row.communication_type}
                          </p>
                        </div>
                        <div className='min-w-0'>
                          <span className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden'>
                            Physician
                          </span>
                          <div className='text-sm text-foreground line-clamp-2'>
                            {physicianNameForRow(row)}
                          </div>
                        </div>
                        <div>
                          <span className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden'>
                            Assigned clinician
                          </span>
                          <div className='text-sm text-foreground'>
                            {assigneeNameForRow(row.assigned_clinician_id)}
                          </div>
                        </div>
                        <div className='min-w-0'>
                          <span className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden'>
                            Communication
                          </span>
                          <LongTextPreview
                            text={row.notes}
                            className='w-full'
                            contentClassName='max-w-lg'
                            align='start'
                            side='top'
                          />
                        </div>
                        <div className='flex justify-end gap-1'>
                          {isLockedCommunication ? (
                            <LockedTaskIndicator
                              taskType='patient_communication'
                              status={row.status}
                            />
                          ) : (
                            <>
                              <Button
                                variant='ghost'
                                size='sm'
                                type='button'
                                className='h-8 w-8 p-0 opacity-70 transition-opacity group-hover:opacity-100'
                                onClick={() => openEdit(row)}
                                aria-label='Edit communication'
                              >
                                <Pencil className='h-4 w-4' />
                              </Button>
                              <Button
                                variant='ghost'
                                size='sm'
                                type='button'
                                onClick={() => setDeleteId(row.id)}
                                className='h-8 w-8 p-0 opacity-70 transition-opacity group-hover:opacity-100'
                                aria-label='Delete communication'
                              >
                                <Trash2 className='h-4 w-4 text-destructive' />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>Add Communication</DialogTitle>
            <DialogDescription>
              Add a dated communication note for this patient.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            {addBlockedByMissingClinic && (
              <Alert className='border-amber-200 bg-amber-50 text-amber-950'>
                <AlertTriangle className='h-4 w-4 text-amber-600' />
                <AlertDescription className='text-sm text-amber-900'>
                  {MISSING_PATIENT_CLINIC_MESSAGE}{' '}
                  {MISSING_PATIENT_CLINIC_DETAIL}
                </AlertDescription>
              </Alert>
            )}
            <div className='space-y-1.5'>
              <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                Date
              </Label>
              <DatePicker
                date={newDate}
                onDateChange={setNewDate}
                disabled={creating || addBlockedByMissingClinic}
              />
            </div>
            <div className='space-y-1.5'>
              <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                Type
              </Label>
              <Select
                value={newCommType}
                onValueChange={v =>
                  setNewCommType(v as PatientCommunicationTypeId)
                }
                disabled={creating || addBlockedByMissingClinic}
              >
                <SelectTrigger className='w-full'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PATIENT_COMMUNICATION_TYPES.map(t => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1.5'>
              <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                Physician <span className='text-destructive'>*</span>
              </Label>
              <Select
                value={newPhysicianId || 'none'}
                onValueChange={v => setNewPhysicianId(v === 'none' ? '' : v)}
                disabled={creating || addBlockedByMissingClinic}
              >
                <SelectTrigger className='w-full'>
                  {newPhysicianId ? (
                    <SelectValue />
                  ) : (
                    <span className='text-muted-foreground'>
                      Select physician
                    </span>
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='none'>Select physician</SelectItem>
                  {physicianOptions.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {formatClinicianName(c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {addNeedsAssignableClinician && (
              <div className='space-y-1.5'>
                <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                  Assigned clinician <span className='text-destructive'>*</span>
                </Label>
                <Select
                  value={newAssignee || 'none'}
                  onValueChange={v => setNewAssignee(v === 'none' ? '' : v)}
                  disabled={creating || addBlockedByMissingClinic}
                >
                  <SelectTrigger className='w-full'>
                    {newAssignee ? (
                      <SelectValue />
                    ) : (
                      <span className='text-muted-foreground'>
                        Select assigned clinician
                      </span>
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='none'>
                      Select assigned clinician
                    </SelectItem>
                    {cliniciansForRows.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {formatClinicianName(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className='space-y-1.5'>
              <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                Communication <span className='text-destructive'>*</span>
              </Label>
              <Textarea
                value={newNotes}
                onChange={e => setNewNotes(e.target.value)}
                placeholder='e.g. Discussed plan with PCP Dr. Smith…'
                className='min-h-[120px] resize-y text-sm'
                disabled={creating || addBlockedByMissingClinic}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setAddOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleCreate()}
              disabled={
                creating ||
                addBlockedByMissingClinic ||
                !newNotes.trim() ||
                !newPhysicianId ||
                (addNeedsAssignableClinician && !newAssignee)
              }
            >
              {creating ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={handleEditClose}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>Edit Communication</DialogTitle>
            <DialogDescription>
              Update the communication details for this patient.
            </DialogDescription>
          </DialogHeader>
          {editingCommunication && (
            <CommunicationVisitEditor
              key={editingCommunication.id}
              communication={editingCommunication}
              assignableClinicians={cliniciansForRows}
              physicianClinicians={physicianOptions}
              formatClinicianName={formatClinicianName}
              isSaving={savingEditId === editingCommunication.id}
              onSave={handleSaveEdit}
              onCancel={() => handleEditClose(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteId}
        onOpenChange={open => !open && setDeleteId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Communication</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this patient communication? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDeleteId(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={() => void handleConfirmDelete()}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientCommunicationsTab;
